import type { CompositionElement } from '../../render/preview-diagnostics';
import type { Rectangle, VideoPresentation } from '../../../../renderer/src/video-presentation';

export interface CompositionIssue {
  kind: 'statistics' | 'title' | 'information';
  title: string; message: string; previewLabel: string; action: string; bounds: Rectangle;
}

export function compositionIssue(elements: readonly CompositionElement[], width: number, height: number): CompositionIssue | undefined {
  const outside = (b: Rectangle) => b.x < -2 || b.y < -2 || b.x + b.width > width + 2 || b.y + b.height > height + 2;
  const problems = elements.filter(e => e.bounds.width > 0 && e.bounds.height > 0 && e.name !== 'Trace marker'
    && (outside(e.bounds) || (e.overflow && /statistics|title|details/i.test(e.name))));
  if (!problems.length) return;
  const statistics = problems.filter(e => /statistics/i.test(e.name));
  const titles = problems.filter(e => /title|details/i.test(e.name));
  const affected = statistics.length ? statistics : titles.length ? titles : problems;
  const left = Math.max(0, Math.min(width - 8, Math.min(...affected.map(e => e.bounds.x))));
  const top = Math.max(0, Math.min(height - 8, Math.min(...affected.map(e => e.bounds.y))));
  const right = Math.max(left + 8, Math.min(width, Math.max(...affected.map(e => e.bounds.x + e.bounds.width))));
  const bottom = Math.max(top + 8, Math.min(height, Math.max(...affected.map(e => e.bounds.y + e.bounds.height))));
  const bounds = { x: left, y: top, width: right - left, height: bottom - top };
  if (statistics.length) return { kind: 'statistics', title: 'Give the statistics more room',
    message: 'Some statistics still do not fit. A two-column layout gives each reading more space.',
    previewLabel: 'Check statistics', action: 'Use two columns', bounds };
  if (titles.length) return { kind: 'title', title: 'Bring the title into the frame',
    message: 'The title is outside its available area. Restore its position and size to keep it visible.',
    previewLabel: 'Check title', action: 'Reset title position and size', bounds };
  return { kind: 'information', title: 'Bring the information into the frame',
    message: 'Some information is outside the video frame. Restore the information layout to keep it visible.',
    previewLabel: 'Check information', action: 'Restore information layout', bounds };
}

export function repairComposition(presentation: VideoPresentation, kind: CompositionIssue['kind']): VideoPresentation {
  if (presentation.version === 2) {
    if (kind === 'statistics') return { ...presentation, layout: 'information-card' };
    if (kind === 'title') return { ...presentation, upperVerticalOffset: 0, lowerVerticalOffset: 0, titleSize: Math.min(44, presentation.titleSize) };
    return { ...presentation, layout: 'information-card', upperVerticalOffset: 0, lowerVerticalOffset: 0,
      titleSize: Math.min(44, presentation.titleSize), statisticsSize: Math.min(30, presentation.statisticsSize) };
  }
  if (kind === 'statistics') return { ...presentation, statisticsLayout: 'two-by-two' };
  if (kind === 'title') return { ...presentation, titlePosition: 'upper', titleScale: Math.min(100, presentation.titleScale) };
  return { ...presentation, layout: 'original', titlePosition: 'upper', titleScale: Math.min(100, presentation.titleScale), statisticsLayout: 'two-by-two', statisticsScale: Math.min(100, presentation.statisticsScale) };
}
